import { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import axios from "axios";
import { mutate } from 'swr';
import store from '../../redux/store';
import PanelSkeleton from "../PanelSkeleton";
import Pagination from "../Pagination";
import { toast } from "react-toastify";
import { FaRegCheckCircle, FaRegTrashAlt, FaToggleOn, FaToggleOff } from "react-icons/fa";
import { ImSpinner2 } from "react-icons/im";
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import styles from "../../styles/productspanel.module.css";
import { FiSave, FiEdit, FiPackage, FiFileText, FiImage, FiVideo, FiRotateCw, FiPlus, FiFilter, FiX, FiGrid, FiList } from 'react-icons/fi';
import { MdClose, MdKeyboardArrowUp, MdKeyboardArrowDown, MdViewInAr } from 'react-icons/md';
import { HiOutlineXCircle } from 'react-icons/hi';
import { BsSearch } from "react-icons/bs";
import ProductImage from "../ProductImage";
import mediaUrl from "../../utils/mediaUrl";

const authConfig = (extra = {}) => {
  const token = store.getState().auth?.token;
  return {
    withCredentials: true,
    ...extra,
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...extra.headers }
  };
};

const ProductsPanel = () => {
  const [product, setProduct] = useState({
    name: "",
    code: "",
    description: "",
    categoryIds: [],
    color: "",
    multi: 0,
    hasAyah: 0
  });
  const [allCategories, setAllCategories] = useState([]);
  const [addProduct, setAddProduct] = useState(false);
  const [allDimensions, setAllDimensions] = useState([]);
  // Single source of truth for product images: existing images carry a server
  // image_url, newly added ones also carry the File in `file`.
  const [previews, setPreviews] = useState([]);
  const [deletedDims, setDeletedDims] = useState([]);
  const [dimensions, setDimensions] = useState([]);
  const [deletedDimArImages, setDeletedDimArImages] = useState([]);
  const [video, setVideo] = useState({file: null, objectUrl: null, cloudinary_id: null});
  const [deletedVid, setDeletedVid] = useState(null);
  const [model, setModel] = useState({file: null, cloudinary_id: null});
  const [deletedModel, setDeletedModel] = useState(null);
  const [arImage, setArImage] = useState({file: null, objectUrl: null, cloudinary_id: null});
  const [deletedArImage, setDeletedArImage] = useState(null);
  const [editingProductId, setEditingProductId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchParams, setSearchParams] = useState(new URLSearchParams());
  const [moveSearch, setMoveSearch] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [searching, setSearching] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [soft, setSoft] = useState(false);
  const [hard, setHard] = useState(false);
  const [show, setShow] = useState({});
  const [nameFocus, setNameFocus] = useState(false);
  const [codeFocus, setCodeFocus] = useState(false);
  const [descFocus, setDescFocus] = useState(false);
  const [mattFocus, setMattFocus] = useState([]);
  const [glossFocus, setGlossFocus] = useState([]);
  const navigate = useNavigate();
  const imgInputRef = useRef(null);
  const vidInputRef = useRef(null);
  const modelInputRef = useRef(null);
  const arImageInputRef = useRef(null);
  const dimArImageInputRefs = useRef({});
  const formRef = useRef(null);
  const inputRef = useRef(null);
  const panelRefs = useRef({});

  const [currentPage, setCurrentPage] = useState(1);

  // ── List filters (all optional, combinable with the search) ──
  const EMPTY_FILTERS = {
    status: "", category: "", color: "", multi: "", hasAyah: "",
    hasAr: "", hasVideo: "", hasSizes: "", minPrice: "", maxPrice: "", sort: "",
  };
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [priceDraft, setPriceDraft] = useState({ minPrice: "", maxPrice: "" });
  const [showFilters, setShowFilters] = useState(false);

  // Cards or table — remembered in this browser
  const VIEW_KEY = "fz_dashboard_products_view";
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem(VIEW_KEY) === "table" ? "table" : "cards"; } catch { return "cards"; }
  });
  const changeView = (mode) => {
    setViewMode(mode);
    try { localStorage.setItem(VIEW_KEY, mode); } catch { /* ignore */ }
  };

  const setFilter = (key, value) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setCurrentPage(1);
  };

  // Price inputs apply after the admin stops typing
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((f) =>
        f.minPrice === priceDraft.minPrice && f.maxPrice === priceDraft.maxPrice
          ? f
          : { ...f, ...priceDraft }
      );
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(t);
  }, [priceDraft]);

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setPriceDraft({ minPrice: "", maxPrice: "" });
    setCurrentPage(1);
  };

  const filterQuery = useMemo(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v !== "") params.set(k, v); });
    return params.toString();
  }, [filters]);

  // Sorting isn't a filter, so it doesn't count towards the badge
  const activeFilterCount = Object.entries(filters).filter(([k, v]) => k !== "sort" && v !== "").length;
  const [productsPerPage] = useState(5);
  const [data, setData] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchDimensions = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/products/dimensions`, authConfig());
      console.log('dimensions response:', res.data);
      setAllDimensions(res.data.data);
      return res.data.data;
    } catch (err) {
      console.error('dimensions error:', err.response?.status, err.response?.data, err.message);
      toast.error(<span className="toast">لم نتمكن من تحميل البيانات. حاول مرة اخرى</span>)
      return [];
    }
  }

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/categories/dashboardFetch`, authConfig());
      setAllCategories(res.data.data);
    } catch {
      toast.error(<span className="toast">لم نتمكن من تحميل التصنيفات. حاول مرة اخرى</span>)
    }
  }

  // Categories are also needed by the list's category filter, not only the form
  useEffect(() => {
    fetchCategories();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const dimOptions = allDimensions.map(dim => ({
    value: { dim_id: dim.id, width: dim.width, height: dim.height, mattePrice: dim.mattePrice, glossPrice: dim.glossPrice },
    label: `${dim.width} × ${dim.height}`
  }));

  // The wall-preview (AR) feature fills the customer's chosen size edge-to-edge
  // (object-fit: cover), so it crops the AR photo to match. This free/general photo
  // is only used for the "مقاس مخصص" (custom size) path — a preset dimension with
  // its own AR photo below uses that instead, at its own exact aspect ratio.
  const arRatioHint = 'تُستخدم هذه الصورة فقط عند اختيار العميل "مقاس مخصص" فى ميزة "شاهدها على حائطك". لأفضل نتيجة، اقتصّها بنسبة أبعاد قريبة من مقاسات اللوحة الشائعة';

  const handleActive = async () => {
    const [dims] = await Promise.all([fetchDimensions(), fetchCategories()]);
    window.scrollTo({top: 0, behavior: 'smooth'});
    setEditingProductId(null);
    // Start with every catalog dimension attached by default (with its suggested
    // prices) — the admin can remove whichever ones don't apply to this product.
    setDimensions(dims.map((dim, index) => ({
      id: Date.now() + index,
      temp: true,
      dim_id: dim.id,
      width: dim.width,
      height: dim.height,
      matte_price: dim.mattePrice,
      gloss_price: dim.glossPrice,
      ar_image_file: null,
      ar_image_object_url: null,
      ar_image_cloudinary_id: null,
    })));
    setPreviews([]);
    setDeletedDims([]);
    setDeletedDimArImages([]);
    setVideo({file: null, objectUrl: null});
    setDeletedVid(null);
    setModel({file: null, cloudinary_id: null});
    setDeletedModel(null);
    setArImage({file: null, objectUrl: null, cloudinary_id: null});
    setDeletedArImage(null);
    setProduct({ name: "", code: "", description: "", categoryIds: [], color: "", multi: 0, hasAyah: 0 });
    setAddProduct(true);
  }

  useEffect(() => {
    if (addProduct && formRef.current) {
      void formRef.current.offsetWidth;
      formRef.current.classList.add("active");
    }
  }, [addProduct]);

  const handleInactive = () => {
    if (addProduct && formRef.current) {
      formRef.current.classList.remove("active");
      setTimeout(() => {
        setAddProduct(false);
      }, 200);
    }
  }

  const categoryOptions = allCategories.map(cat => ({
    value: cat.id,
    label: cat.name,
  }));

  const color = [
    { value: 'ترابية', label: 'الوان ترابية' },
    { value: 'فاتحة', label: 'الوان فاتحة' },
    { value: 'داكنة', label: 'الوان داكنة' },
    { value: 'الوان', label: 'الوان' },
    { value: 'أسود', label: 'أسود' },
    { value: 'أبيض', label: 'أبيض' }
  ];

  // A product can be in several categories
  const selectedCategories = categoryOptions.filter(opt => product.categoryIds.includes(opt.value));

  const selectedColor = color.find(col => col.value === product.color);

  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      color: state.isSelected ? '#e1ae8b' : '',
      fontWeight: state.isSelected ? 'bold' : 'normal'
    })
  };

  useEffect(() => {
    let lastScrollY = window.scrollY;
  
    const handleScroll = () => {
      if (window.scrollY > lastScrollY) {
        setMoveSearch(true);
      } else {
        setMoveSearch(false);
      }
      lastScrollY = window.scrollY;
    };
  
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleClear = () => {
    setSearchTerm("");
    inputRef.current.focus();
  }

  useEffect(() => {
    if (!searchTerm) {
      setSearchParams(new URLSearchParams());
    }
  }, [searchTerm])

  const handleSearch = async (e) => {
    e.preventDefault();

    const searchTerms = searchTerm.trim().split(/\s+/).filter(Boolean);

    if (!searchTerms) return;

    const newParams = new URLSearchParams();
    searchTerms.forEach(term => newParams.append('search', term));

    if (searchParams.toString() !== newParams.toString()) {
      setSearching(true);
      setSearchParams(newParams);
    }
  }

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/api/products/dashboardFetch?page=${currentPage}&limit=${productsPerPage}&${searchParams.toString()}&${filterQuery}`,
      authConfig()
    )
      .then(res => {
        if (!cancelled) {
          setData(res.data);
          setFetchError(null);
          setSearching(false);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setFetchError(err);
          setSearching(false);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [currentPage, searchParams, refreshKey, filterQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const isLoadingInitial = isLoading;

  const products = data?.products || [];
  const totalProducts = data?.totalCount ? Number(data?.totalCount) : 0;
  const totalPages = Math.ceil(totalProducts / productsPerPage);
  
  const toggleExpand = (id) => {
    const panel = panelRefs.current[id];
    const upperPanel = panel.querySelector(".upperPanel");

    if (expanded[id]) {
      panel.style.height = `${panel.scrollHeight}px`;

      void panel.offsetHeight;

      panel.style.height = `${upperPanel.scrollHeight + 20}px`;

      setTimeout(() => {
        setExpanded(prev => ({...prev, [id]: false}));
      }, 500);
    } else {
      panel.style.height = `${upperPanel.scrollHeight + 20}px`;

      void panel.offsetHeight;

      setExpanded(prev => ({...prev, [id]: true}));

      setTimeout(() => {
        panel.style.height = `${panel.scrollHeight}px`;
      }, 10);
    }
  };

  const toggleShow = (id) => {
    if (show[id]) {
      setShow(prev => ({...prev, [id]: false}));
    } else {
      setShow(prev => ({...prev, [id]: true}));
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target;

    setProduct({ ...product, [name]: value});
  };

  const addInputDimensions = () => {
    setDimensions((prev) => [...prev, {
      id: Date.now(),
      temp: true,
      dim_id: "",
      width: "",
      height: "",
      matte_price: "",
      gloss_price: "",
      ar_image_file: null,
      ar_image_object_url: null,
      ar_image_cloudinary_id: null,
    }])
  };

  const removeInputDimensions = (index, dim) => {
    if (!dim.temp && dim.id) {
      setDeletedDims(prev => [...prev, dim.id]);
    }
    if (dim.ar_image_object_url && dim.ar_image_file) {
      URL.revokeObjectURL(dim.ar_image_object_url);
    }

    setDimensions((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDimArImageChange = (index, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error(<span className="toast">حجم الصورة يجب أن يكون أقل من 5 ميجابايت</span>);
      e.target.value = '';
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setDimensions(prev => prev.map((d, i) =>
      i === index ? { ...d, ar_image_file: file, ar_image_object_url: objectUrl } : d
    ));
    e.target.value = '';
  };

  // Clearing a saved AR photo (not one just staged locally) tells the server to drop
  // it explicitly — dim.ar_image_cloudinary_id always reflects the server's copy,
  // untouched by handleDimArImageChange, so it stays a reliable "was there one saved?" check.
  const removeDimArImage = (index, dim) => {
    if (dim.ar_image_cloudinary_id) {
      setDeletedDimArImages(prev => prev.includes(dim.id) ? prev : [...prev, dim.id]);
    }
    if (dim.ar_image_object_url && dim.ar_image_file) {
      URL.revokeObjectURL(dim.ar_image_object_url);
    }
    setDimensions(prev => prev.map((d, i) =>
      i === index ? { ...d, ar_image_file: null, ar_image_object_url: null, ar_image_cloudinary_id: null } : d
    ));
  };

  const handleDimensionChange = (index, e) => {
    const { name, value } = e.target;
    
    setDimensions(prev => prev.map((dim, i) => 
      i === index ? { ...dim, [name]: value } : dim
    ));
  };

  const removeInput = (index) => {
    setPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleImageChange = (e) => {
    const files = e.target.files;

    if (files) {
      const totalImages = previews.length + files.length;

      if (files.length > 8 || totalImages > 8) {
        toast.error(<span className="toast">حد أقصى 8 صور لكل لوحة</span>);
        e.target.value = '';
        return;
      }

      const filesArray = Array.from(files);

      const oversized = filesArray.find(file => file.size > 3 * 1024 * 1024);

      if (oversized) {
        toast.error(<span className="toast">حجم الملف يجب أن يكون أقل من 3 ميجابايت</span>);
        e.target.value = '';
        return;
      }

      filesArray.forEach((file) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviews(prev => [
            ...prev,
            {
              image_url: reader.result,
              is_main: false,
              file
            }
          ]);
        };
        reader.readAsDataURL(file);
      });
    };
  };

  const markAsMain = (index) => {
    setPreviews(prev => prev.map((img, i) => ({
      ...img, is_main: i === index
    })))
  };

  const handleVideoChange = async (e) => {
    const files = e.target.files;

    if (files) {
      if (files.length > 1) {
        toast.error(<span className="toast">حد أقصى فيديو واحد لكل لوحة</span>);
        e.target.value = '';
        return;
      }

      const file = files[0];

      const duration = await getVideoDurationFromFile(file);

      if (duration > 30) {
        toast.error(<span className="toast">مدة الفيديو يجب أن تكون أقل من 30 ثانية</span>);
        e.target.value = '';
        return;
      }

      const oversized = file.size > 50 * 1024 * 1024;

      if (oversized) {
        toast.error(<span className="toast">حجم الفيديو يجب أن يكون أقل من 50 ميجابايت</span>);
        e.target.value = '';
        return;
      }

      const objectUrl = URL.createObjectURL(file);

      setVideo({ 
        file: file, 
        objectUrl: objectUrl,
        cloudinary_id: null
      });

      e.target.value = '';
    };
  };

  const getVideoDurationFromFile = (file) => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      const url = URL.createObjectURL(file);
      
      video.preload = 'metadata';
      video.src = url;
      
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        resolve(video.duration);
      };
      
      video.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(0);
      };
      
      video.load();
    });
  };

  const handleModelChange = (e) => {
    const files = e.target.files;

    if (files) {
      if (files.length > 1) {
        toast.error(<span className="toast">حد أقصى موديل واحد لكل لوحة</span>);
        e.target.value = '';
        return;
      }

      const file = files[0];

      const oversized = file.size > 5 * 1024 * 1024;

      if (oversized) {
        toast.error(<span className="toast">حجم الملف يجب أن يكون أقل من 5 ميجابايت</span>);
        e.target.value = '';
        return;
      }

      setModel({file: file, cloudinary_id: null});

      e.target.value = '';
    };
  };

  const handleArImageChange = (e) => {
    const files = e.target.files;

    if (files) {
      if (files.length > 1) {
        toast.error(<span className="toast">حد أقصى صورة واحدة لكل لوحة</span>);
        e.target.value = '';
        return;
      }

      const file = files[0];

      const oversized = file.size > 5 * 1024 * 1024;

      if (oversized) {
        toast.error(<span className="toast">حجم الصورة يجب أن يكون أقل من 5 ميجابايت</span>);
        e.target.value = '';
        return;
      }

      const objectUrl = URL.createObjectURL(file);

      // Replacing an already-saved image: stage the old one for removal since
      // it's about to be swapped out, not just added alongside.
      if (arImage.cloudinary_id) {
        setDeletedArImage(arImage.cloudinary_id);
      }

      setArImage({file: file, objectUrl: objectUrl, cloudinary_id: null});

      e.target.value = '';
    };
  };

  // Turn a preview entry into a File: new images already carry one,
  // existing images are fetched back from the server as a blob.
  const previewToFile = async (img, index) => {
    if (img.file) return img.file;
    const res = await fetch(mediaUrl(img.image_url));
    if (!res.ok) throw new Error(`Failed to fetch image: ${img.image_url}`);
    const blob = await res.blob();
    const name = img.image_url.split('/').pop()?.split('?')[0] || `image_${index}.webp`;
    return new File([blob], name, { type: blob.type || 'image/webp' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!product.categoryIds.length) {
      toast.error(<span className="toast">اختر فئة واحدة على الأقل</span>);
      return;
    }

    if (editingProductId && previews.length === 0) {
      toast.error(<span className="toast">يجب أن تحتوي اللوحة على صورة واحدة على الأقل</span>);
      return;
    }

    // Ensure exactly one main image; default to the first one.
    const submittedImages = previews.some(img => img.is_main)
      ? previews
      : previews.map((img, index) => ({ ...img, is_main: index === 0 }));

    let submittedFiles;
    try {
      submittedFiles = await Promise.all(submittedImages.map(previewToFile));
    } catch {
      toast.error(<span className="toast">لم نتمكن من تجهيز الصور. حاول مرة اخرى</span>);
      return;
    }

    const updatedDimensions = dimensions.map(dim => ({
      ...(dim.temp ? {} : { id: dim.id }),
      dim_id: dim.dim_id,
      width: dim.width,
      height: dim.height,
      matte_price: dim.matte_price,
      gloss_price: dim.gloss_price,
    }));

    const formData = new FormData();
    formData.append("name", product.name.trim().replace(/\s+/g, ' '));
    if (editingProductId) {
      formData.append("code", product.code);
    }
    formData.append("description", product.description.trim().replace(/\s+/g, ' '));
    formData.append("categoryIds", JSON.stringify(product.categoryIds));
    formData.append("color", product.color);
    formData.append("multi", product.multi);
    formData.append("hasAyah", product.hasAyah);
    
    formData.append("dimensions", JSON.stringify(updatedDimensions));
    formData.append("deletedDims", JSON.stringify(deletedDims));

    dimensions.forEach((dim, index) => {
      if (dim.ar_image_file) {
        formData.append(`dimArImage_${index}`, dim.ar_image_file);
      }
    });
    formData.append("deletedDimArImages", JSON.stringify(deletedDimArImages));

    submittedFiles.forEach((file, index) => {
      formData.append("files", file);
      formData.append(`is_main_${index}`, submittedImages[index].is_main.toString());
    });

    if (video.file !== null) {
      formData.append("videoFile", video.file);
    };

    formData.append("deletedVid", deletedVid);

    if (model.file !== null) {
      formData.append("modelFile", model.file);
    };

    formData.append("deletedModel", deletedModel);

    if (arImage.file !== null) {
      formData.append("arImageFile", arImage.file);
    };

    formData.append("deletedArImage", deletedArImage);

    try {
      if (editingProductId) {
          confirmAlert({
            message: `تحديث ${product.name} ؟`,
            buttons: [
              {
                label: 'نعم',
                onClick: async () => {
                  setSubmitting(true);
                  try {
                    await axios.put(
                      `${process.env.REACT_APP_BACKEND_URL}/api/products/${editingProductId}`,
                      formData,
                      authConfig()
                    );
  
                    toast.success(<span className="toast">تم تحديث {product.name} بنجاح</span>);

                    setRefreshKey(k => k + 1);
                    mutate((key) => key.includes('/api/products/bestselling?page=1'), undefined, { revalidate: false });
                    mutate((key) => key.includes('/api/products/category') && key.includes('page=1'), undefined, { revalidate: false });

                    resetForm();
                    setAddProduct(false);
                  } catch (error) {
                    toast.error(<span className="toast">لم نتمكن من تحديث اللوحة. حاول مرة اخرى</span>);
                  } finally {
                    setSubmitting(false);
                  }
                }
              },
              {
                label: 'لا'
              }
            ]
          });
      } else {
          confirmAlert({
            message: `تأكيد إضافة اللوحة ؟`,
            buttons: [{
              label: 'نعم',
              onClick: async () => {
                setSubmitting(true);
                try {
                  await axios.post(
                    `${process.env.REACT_APP_BACKEND_URL}/api/products/add`, formData, authConfig()
                  );
  
                  toast.success(<span className="toast">تم إضافة اللوحة بنجاح</span>);

                  setRefreshKey(k => k + 1);
                  mutate((key) => key.includes('/api/products/bestselling?page=1'), undefined, { revalidate: false });
                  mutate((key) => key.includes('/api/products/category') && key.includes('page=1'), undefined, { revalidate: false });

                  resetForm();
                  setAddProduct(false);
                } catch (error) {
                  toast.error(<span className="toast">لم نتمكن من إضافة اللوحة. حاول مرة اخرى</span>);
                } finally {
                  setSubmitting(false);
                }
              }
            }, 
            {
              label: 'لا'
            }]
          });
      }
    } catch (error) {
      toast.error(<span className="toast">حدث خطأ غير متوقع. يرجى المحاولة مرة أخرى</span>);
    }
  };

  const resetForm = () => {
    setProduct({ name: "", code: "", description: "", categoryIds: [], color: "", multi: 0, hasAyah: 0 });
    setDimensions([]);
    setPreviews([]);
    setDeletedDims([]);
    setDeletedDimArImages([]);
    setVideo({file: null, objectUrl: null, cloudinary_id: null});
    setDeletedVid(null);
    setModel({file: null, cloudinary_id: null});
    setDeletedModel(null);
    setArImage({file: null, objectUrl: null, cloudinary_id: null});
    setDeletedArImage(null);
    setEditingProductId(null);
  };

  const handleEdit = async (product) => {
    await Promise.all([fetchDimensions(), fetchCategories()]);
    setAddProduct(true);
    setEditingProductId(product.product_id);
    setDeletedDims([]);
    setDeletedDimArImages([]);
    setVideo({file: null, objectUrl: product.video?.video_url || null, cloudinary_id: product.video?.cloudinary_id || null});
    setDeletedVid(null);
    setModel({file: null, cloudinary_id: product.arModel?.cloudinary_id || null});
    setDeletedModel(null);
    setArImage({file: null, objectUrl: product.arImage?.image_url || null, cloudinary_id: product.arImage?.cloudinary_id || null});
    setDeletedArImage(null);
    setProduct({
      name: product.name,
      code: product.code,
      description: product.description,
      categoryIds: product.categoryIds ?? (product.categoryId ? [product.categoryId] : []),
      color: product.color,
      // The API sends true/false; the form toggles use 0/1
      multi: product.multi ? 1 : 0,
      hasAyah: product.has_ayah ? 1 : 0
    });

    if (product.dimensions) {
      const formattedDimensions = product.dimensions.map(dim => ({
        id: dim.id,
        dim_id: dim.dim_id,
        width: dim.width,
        height: dim.height,
        matte_price: dim.matte_price,
        gloss_price: dim.gloss_price,
        ar_image_file: null,
        ar_image_object_url: dim.ar_image_url || null,
        ar_image_cloudinary_id: dim.ar_image_cloudinary_id || null,
      }));
      setDimensions(formattedDimensions);
    } else {
      setDimensions([]);
    }

    if (product.images?.length) {
      setPreviews(product.images.map(img => ({
        image_url: img.image_url,
        // The API sends true/false (older data may use 1/0)
        is_main: Boolean(img.is_main)
      })));
    } else {
      setPreviews([]);
    }
  };

  const handleSoftDelete = async (product) => {    
    confirmAlert({
      message: `حذف ${product.name} مؤقتاً ؟`,
      buttons: [
        {
          label: 'نعم',
          onClick: async () => {
            setSoft(true);
            try {
              handleInactive();

              await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/products/${product.product_id}/delete`, null, authConfig());
              toast.success(<span className="toast">تم حذف {product.name} مؤقتاً بنجاح</span>);

              setRefreshKey(k => k + 1);
              mutate((key) => key.includes('/api/products/bestselling?page=1'), undefined, { revalidate: false });
              mutate((key) => key.includes('/api/products/category') && key.includes('page=1'), undefined, { revalidate: false });

              setShow(prev => ({...prev, [product.product_id]: false}));
            } catch (error) {
              toast.error(<span className="toast">لم نتمكن من حذف اللوحة. حاول مرة اخرى</span>);
            } finally {
              setSoft(false);
            }
          }
        },
        {
          label: 'لا'
        }
      ]
    });
  };

  const handleHardDelete = async (product) => {
    const cloudinaryIds = product.images.map(img => img.cloudinary_id).filter(Boolean);
    const videoCloudinaryId = product.video?.cloudinary_id;
    const modelCloudinaryId = product.arModel?.cloudinary_id;
    const arImageCloudinaryId = product.arImage?.cloudinary_id;
    const dimArImageCloudinaryIds = (product.dimensions || [])
      .map(dim => dim.ar_image_cloudinary_id)
      .filter(Boolean);

    confirmAlert({
      message: `حذف ${product.name} نهائياً ؟`,
      buttons: [
        {
          label: 'نعم',
          onClick: async () => {
            setHard(true);
            try {
              handleInactive();
              setShow(prev => ({...prev, [product.product_id]: false}));

              await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/products/${product.product_id}`, authConfig({ data: { cloudinaryIds, videoCloudinaryId, modelCloudinaryId, arImageCloudinaryId, dimArImageCloudinaryIds } }));
              toast.success(<span className="toast">تم حذف {product.name} نهائياً بنجاح</span>);

              setRefreshKey(k => k + 1);
              mutate((key) => key.includes('/api/products/bestselling?page=1'), undefined, { revalidate: false });
              mutate((key) => key.includes('/api/products/category') && key.includes('page=1'), undefined, { revalidate: false });
            } catch (error) {
              toast.error(<span className="toast">لم نتمكن من حذف اللوحة. حاول مرة اخرى</span>);
            } finally {
              setHard(false);
            }
          }
        },
        {
          label: 'لا'
        }
      ]
    });
  };

  const handleRestore = async (product) => {
    confirmAlert({
      message: `إعادة ${product.name} ؟`,
      buttons: [
        {
          label: 'نعم',
          onClick: async () => {
            setRestoring(true);
            try {
              await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/products/${product.product_id}/restore`, null, authConfig());
              toast.success(<span className="toast">تم إعادة {product.name} بنجاح</span>);

              setRefreshKey(k => k + 1);
              mutate((key) => key.includes('/api/products/bestselling?page=1'), undefined, { revalidate: false });
              mutate((key) => key.includes('/api/products/category') && key.includes('page=1'), undefined, { revalidate: false });

            } catch (error) {
              toast.error(<span className="toast">لم نتمكن من إعادة اللوحة. حاول مرة اخرى</span>);
            } finally {
              setRestoring(false);
            }
          }
        },
        {
          label: 'لا'
        }
      ]
    });
  };

  useEffect(() => {
    return () => {
      previews.forEach(url => URL.revokeObjectURL(url));
    };
  }, [previews]);

  useEffect(() => {
    return () => {
      URL.revokeObjectURL(video.objectUrl);
    };
  }, [video]);

  if (fetchError) {
    return <p className="warn">حدث خطأ اثناء تحميل البيانات. حاول مرة اخرى</p>
  };

  return (
    <div className={styles.panel}>
      <form onSubmit={handleSearch} className={moveSearch ? "search move" : "search"}>
        <input
          type="text"
          placeholder="بحث فى اللوحات . . ."
          maxLength={30}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          ref={inputRef}
          required
        />
        {searchTerm && (
          <MdClose className="clear" onClick={handleClear} />
        )}
        <button
          type="submit"
          disabled={searching}
          className="buttonNoBorder"
        >{searching ?
          <ImSpinner2 className="smallspinner" /> :
          <BsSearch className="searchiconsmall" />
        }</button>
      </form>

      <div className={styles.toolbar}>
        <span className={styles.toolbarTitle}>إدارة اللوحات</span>
        {addProduct ? (
          <button
            onClick={handleInactive}
            disabled={submitting || soft || hard || restoring}
            className={styles.cancelBtnInline}
          >
            × إلغاء
          </button>
        ) : (
          <button
            onClick={handleActive}
            disabled={submitting || soft || hard || restoring}
            className={styles.addBtnInline}
          >
            <FiPlus /> إضافة لوحة
          </button>
        )}
      </div>

      {/* ── Filters ── */}
      {!addProduct && (
        <div className={styles.filters}>
          <div className={styles.filtersTop}>
            <button
              type="button"
              className={`${styles.filtersToggle} ${showFilters ? styles.filtersToggleOn : ""}`}
              onClick={() => setShowFilters((v) => !v)}
              aria-expanded={showFilters}
            >
              <FiFilter /> الفلاتر
              {activeFilterCount > 0 && <span className={styles.filtersCount}>{activeFilterCount}</span>}
            </button>

            <label className={styles.sortWrap}>
              <span>ترتيب</span>
              <select value={filters.sort} onChange={(e) => setFilter("sort", e.target.value)} className={styles.filterSelect}>
                <option value="">الأحدث</option>
                <option value="oldest">الأقدم</option>
                <option value="bestselling">الأكثر مبيعاً</option>
                <option value="name">الاسم</option>
              </select>
            </label>

            <div className={styles.viewToggle} role="group" aria-label="طريقة العرض">
              <button
                type="button"
                className={viewMode === "cards" ? styles.viewToggleOn : ""}
                onClick={() => changeView("cards")}
                aria-pressed={viewMode === "cards"}
                title="عرض البطاقات"
              >
                <FiGrid />
              </button>
              <button
                type="button"
                className={viewMode === "table" ? styles.viewToggleOn : ""}
                onClick={() => changeView("table")}
                aria-pressed={viewMode === "table"}
                title="عرض الجدول"
              >
                <FiList />
              </button>
            </div>

            <span className={styles.resultsCount}>{totalProducts} لوحة</span>

            {activeFilterCount > 0 && (
              <button type="button" className={styles.clearFilters} onClick={clearFilters}>
                <FiX /> مسح الفلاتر
              </button>
            )}
          </div>

          {showFilters && (
            <div className={styles.filtersGrid}>
              <label className={styles.filterField}>
                <span>الحالة</span>
                <select value={filters.status} onChange={(e) => setFilter("status", e.target.value)} className={styles.filterSelect}>
                  <option value="">الكل</option>
                  <option value="active">نشطة</option>
                  <option value="deleted">محذوفة</option>
                </select>
              </label>

              <label className={styles.filterField}>
                <span>الفئة</span>
                <select value={filters.category} onChange={(e) => setFilter("category", e.target.value)} className={styles.filterSelect}>
                  <option value="">الكل</option>
                  {allCategories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </label>

              <label className={styles.filterField}>
                <span>اللون</span>
                <select value={filters.color} onChange={(e) => setFilter("color", e.target.value)} className={styles.filterSelect}>
                  <option value="">الكل</option>
                  {color.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </label>

              <label className={styles.filterField}>
                <span>نوع اللوحة</span>
                <select value={filters.multi} onChange={(e) => setFilter("multi", e.target.value)} className={styles.filterSelect}>
                  <option value="">الكل</option>
                  <option value="false">لوحة واحدة</option>
                  <option value="true">لوحات متعددة</option>
                </select>
              </label>

              <label className={styles.filterField}>
                <span>الآية</span>
                <select value={filters.hasAyah} onChange={(e) => setFilter("hasAyah", e.target.value)} className={styles.filterSelect}>
                  <option value="">الكل</option>
                  <option value="true">مع آية</option>
                  <option value="false">بدون آية</option>
                </select>
              </label>

              <label className={styles.filterField}>
                <span>صورة AR</span>
                <select value={filters.hasAr} onChange={(e) => setFilter("hasAr", e.target.value)} className={styles.filterSelect}>
                  <option value="">الكل</option>
                  <option value="true">بها صورة</option>
                  <option value="false">بدون صورة</option>
                </select>
              </label>

              <label className={styles.filterField}>
                <span>فيديو</span>
                <select value={filters.hasVideo} onChange={(e) => setFilter("hasVideo", e.target.value)} className={styles.filterSelect}>
                  <option value="">الكل</option>
                  <option value="true">به فيديو</option>
                  <option value="false">بدون فيديو</option>
                </select>
              </label>

              <label className={styles.filterField}>
                <span>المقاسات</span>
                <select value={filters.hasSizes} onChange={(e) => setFilter("hasSizes", e.target.value)} className={styles.filterSelect}>
                  <option value="">الكل</option>
                  <option value="true">لها مقاسات</option>
                  <option value="false">بدون مقاسات</option>
                </select>
              </label>

              <div className={`${styles.filterField} ${styles.priceField}`}>
                <span>السعر (Matte) ₪</span>
                <div className={styles.priceInputs}>
                  <input
                    type="number"
                    min={0}
                    placeholder="من"
                    value={priceDraft.minPrice}
                    onChange={(e) => setPriceDraft((p) => ({ ...p, minPrice: e.target.value }))}
                    className={styles.filterSelect}
                  />
                  <span className={styles.priceDash}>—</span>
                  <input
                    type="number"
                    min={0}
                    placeholder="إلى"
                    value={priceDraft.maxPrice}
                    onChange={(e) => setPriceDraft((p) => ({ ...p, maxPrice: e.target.value }))}
                    className={styles.filterSelect}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {(addProduct && allDimensions.length > 0 && allCategories.length > 0) ?
        <form onSubmit={handleSubmit} encType="multipart/form-data" ref={formRef} className="form center">
          <h4>{editingProductId ? "تحديث لوحة" : "إضافة لوحة جديدة"}</h4>

          <div className={styles.namecode}>
            <div className="title">
              <label className={nameFocus || product.name ? "move" : ""}>اسم اللوحة</label>
              <input
                type="text"
                name="name"
                value={product.name}
                onChange={handleChange}
                onFocus={() => setNameFocus(true)}
                onBlur={() => setNameFocus(false)}
                maxLength={100}
                required
              />
            </div>

            <div className="title">
              <label className={codeFocus || product.code || !editingProductId ? "move" : ""}>الكود</label>
              <input
                type="text"
                name="code"
                value={product.code}
                onChange={handleChange}
                onFocus={() => setCodeFocus(true)}
                onBlur={() => setCodeFocus(false)}
                maxLength={20}
                placeholder={editingProductId ? undefined : "سيتم توليده تلقائياً"}
                disabled={!editingProductId}
                required={!!editingProductId}
              />
            </div>
          </div>

          <div className="title">
            <label className={descFocus || product.description ? "move" : ""}>الوصف</label>
            <textarea
              name="description"
              value={product.description}
              onChange={handleChange}
              onFocus={() => setDescFocus(true)}
              onBlur={() => setDescFocus(false)}
              maxLength={255}
              required
            />
          </div>

          <div className="selectCont addSelectCont center">
            <Select
              options={categoryOptions}
              value={selectedCategories}
              isMulti
              closeMenuOnSelect={false}
              onChange={(selected) => {
                setProduct(prev => ({ ...prev, categoryIds: (selected ?? []).map(opt => opt.value) }))
              }}
              placeholder="الفئات (يمكن اختيار أكثر من فئة)"
              className="addSelect"
              classNamePrefix="react-select"
              styles={customStyles}
              isSearchable={false}
              required
            />

            <Select
              options={color}
              defaultValue={selectedColor}
              onChange={(newValue) => {
                setProduct(prev => ({ ...prev, color: newValue.value }))}
              }
              placeholder="الالوان"
              className="addSelect"
              classNamePrefix="react-select"
              styles={customStyles}
              isSearchable={false}
              required
            />
          </div>

          <div className={styles.sectionLabel}>
            <FiFileText /> <span>المقاسات والأسعار</span>
          </div>

          {dimensions.map((dim, index) => (
            <div key={dim.id} className={styles.adddimensions}>
              <p>تفاصيل المقاس {index + 1}</p>
              <div className={styles.dimSelect}>
                <Select
                  options={dimOptions}
                  defaultValue={dimOptions.find(opt => opt.value.dim_id === dim.dim_id)}
                  onChange={(newValue) => {setDimensions(prev => prev.map((dim, i) =>
                    i === index ? {
                      ...dim,
                      dim_id: newValue.value.dim_id,
                      width: newValue.value.width,
                      height: newValue.value.height,
                      matte_price: newValue.value.mattePrice,
                      gloss_price: newValue.value.glossPrice,
                    } : dim
                  ))}}
                  placeholder="المقاس"
                  classNamePrefix="react-select"
                  styles={customStyles}
                  isSearchable={false}
                  required
                />
              </div>

              <div className="title">
                <label className={mattFocus[index] || dim.matte_price ? "move" : ""}>المط</label>
                <input
                  type="number"
                  step={0.01}
                  min={1}
                  max={100000}
                  name="matte_price"
                  value={dim.matte_price}
                  onChange={(e) => handleDimensionChange(index, e)}
                  onFocus={() => {
                    setMattFocus(prev => {
                      const newState = [...prev];
                      newState[index] = true;
                      return newState;
                    });
                  }}
                  onBlur={() => {
                    setMattFocus(prev => {
                      const newState = [...prev];
                      newState[index] = false;
                      return newState;
                    });
                  }}
                  required
                />
              </div>

              <div className="title">
                <label className={glossFocus[index] || dim.gloss_price ? "move" : ""}>اللامع</label>
                <input
                  type="number"
                  step={0.01}
                  min={1}
                  max={100000}
                  name="gloss_price"
                  value={dim.gloss_price}
                  onChange={(e) => handleDimensionChange(index, e)}
                  onFocus={() => {
                    setGlossFocus(prev => {
                      const newState = [...prev];
                      newState[index] = true;
                      return newState;
                    });
                  }}
                  onBlur={() => {
                    setGlossFocus(prev => {
                      const newState = [...prev];
                      newState[index] = false;
                      return newState;
                    });
                  }}
                  required
                />
              </div>

              <div className={styles.dimArImage}>
                {(dim.ar_image_file || dim.ar_image_object_url) ? (
                  <div className="paneldet space" style={{flexDirection: "row"}}>
                    <ProductImage url={dim.ar_image_object_url} alt={`AR للمقاس ${index + 1}`} loading="lazy" />
                    <FaRegTrashAlt
                      className="delete deleteMini cursor"
                      onClick={() => {
                        if (submitting) return;
                        removeDimArImage(index, dim);
                      }}
                    />
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => dimArImageInputRefs.current[dim.id]?.click()}
                    disabled={submitting}
                    className={styles.addBtnSmall}
                  >
                    <MdViewInAr className="icon" /> صورة AR لهذا المقاس (اختياري)
                  </button>
                )}

                <input
                  type="file"
                  accept="image/*"
                  ref={(el) => { dimArImageInputRefs.current[dim.id] = el; }}
                  onChange={(e) => handleDimArImageChange(index, e)}
                  style={{display: "none"}}
                />
              </div>

              <FaRegTrashAlt
                className="delete cursor"
                onClick={() => {
                  if (submitting) return;
                  removeInputDimensions(index, dim);
                }}
              />
            </div>
          ))}

          <button
            type="button"
            onClick={addInputDimensions}
            disabled={dimensions.length >= 6 || submitting}
            className={styles.addBtn}
          >
            <FiFileText className="icon" /> إضافة تفاصيل
          </button>

          <div className={styles.sectionLabel}>
            <FiImage /> <span>صور اللوحة</span>
          </div>

          {previews.length > 0 &&
            <div className="prodcont">
              {previews.map((p, index) => (
                <div key={index} className="preview">
                  <div className="image">
                    <ProductImage
                      url={p.image_url}
                      alt={`Preview ${index}`}
                      loading="lazy"
                    />
                  </div>

                  <div className="space prevactions">
                    <FaRegCheckCircle
                      onClick={() => {
                        if (submitting) return;
                        markAsMain(index);
                      }}
                      className={p.is_main === true ? `cursor ${styles.true} ${styles.ismain}` : `cursor ${styles.ismain}`}
                    />

                    <FaRegTrashAlt
                      className="delete deleteMini cursor"
                      onClick={() => {
                        if (submitting) return;
                        removeInput(index);
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          }

          <input
            type="file"
            accept="image/*"
            multiple
            ref={imgInputRef}
            onChange={handleImageChange}
            style={{display: "none"}}
          />

          <button
            type="button"
            onClick={() => imgInputRef.current.click()}
            disabled={previews.length >= 8 || submitting}
            className={styles.addBtn}
          >
            <FiImage className="icon" /> إضافة صور
          </button>

          {/* <div className={styles.sectionLabel}>
            <FiVideo /> <span>فيديو وموديل ثلاثي الأبعاد</span>
          </div> */}

          {/* {(video.file !== null || video.objectUrl !== null) &&
            <div>
              <video
                src={mediaUrl(video.objectUrl)}
                controls
                controlsList="nodownload noremoteplayback"
                preload="metadata"
                playsInline
              >
                <p>Video type is not supported in your browser</p>
              </video>

              <div className="space prevactions">
                <div></div>

                <FaRegTrashAlt
                  className="delete deleteMini cursor"
                  onClick={() => {
                    if (submitting) return;
                    setDeletedVid(video.file === null ? video.cloudinary_id : null);
                    setVideo({file: null, objectUrl: null});
                  }}
                />
              </div>
            </div>
          } */}

          {/* <input
            type="file"
            accept="video/*"
            ref={vidInputRef}
            onChange={handleVideoChange}
            style={{display: "none"}}
          />

          <button
            type="button"
            onClick={() => vidInputRef.current.click()}
            disabled={video.file || video.objectUrl || submitting}
            className={styles.addBtn}
          >
            <FiVideo className="icon" /> إضافة فيديو
          </button> */}

          <div className={styles.addModel}>
          {(model.file !== null || model.cloudinary_id !== null) &&
            <div className="paneldet space" style={{flexDirection: "row"}}>
              <p>{model.file?.name || model.cloudinary_id.split('/').pop()}</p>
              <FaRegTrashAlt
                className="delete deleteMini"
                onClick={() => {
                  if (submitting) return;
                  setDeletedModel(model.file === null ? model.cloudinary_id : null);
                  setModel({file: null, cloudinary_id: null})
                }}
              />
            </div>
          }

          <input
            type="file"
            accept=".glb"
            ref={modelInputRef}
            onChange={handleModelChange}
            style={{display: "none"}}
          />

          {/* <button
            type="button"
            onClick={() => modelInputRef.current.click()}
            disabled={model.file || model.cloudinary_id || submitting}
            className={styles.addBtn}
          >
            <FiPackage className="icon" /> إضافة موديل AR
          </button> */}
          </div>

          <div className={styles.sectionLabel}>
            <MdViewInAr /> <span>صورة شاهدها على حائطك</span>
          </div>

          {arRatioHint && <p className={styles.arRatioHint}>{arRatioHint}</p>}

          <div className={styles.addModel}>
          {(arImage.file !== null || arImage.cloudinary_id !== null) &&
            <div className="paneldet space" style={{flexDirection: "row"}}>
              <ProductImage url={arImage.objectUrl} alt="AR" loading="lazy" />
              <FaRegTrashAlt
                className="delete deleteMini"
                onClick={() => {
                  if (submitting) return;
                  setDeletedArImage(arImage.file === null ? arImage.cloudinary_id : null);
                  setArImage({file: null, objectUrl: null, cloudinary_id: null})
                }}
              />
            </div>
          }

          <input
            type="file"
            accept="image/*"
            ref={arImageInputRef}
            onChange={handleArImageChange}
            style={{display: "none"}}
          />

          <button
            type="button"
            onClick={() => arImageInputRef.current.click()}
            disabled={submitting}
            className={styles.addBtn}
          >
            <MdViewInAr className="icon" />
            {(arImage.file !== null || arImage.cloudinary_id !== null) ? 'تغيير الصورة' : 'إضافة صورة لميزة "شاهدها على حائطك"'}
          </button>
          </div>

          <div className={`space ${styles.multi}`}>
            <p>لوحات متعددة؟</p>
            <button type="button" onClick={() => setProduct({...product, multi: product.multi === 0 ? 1 : 0})}>
              {product.multi === 0 ? <FaToggleOff className="off" /> :
                <FaToggleOn className="on" />
              }
            </button>
          </div>

          <div className={`space ${styles.multi}`}>
            <p>مع آية؟</p>
            <button type="button" onClick={() => setProduct({...product, hasAyah: product.hasAyah === 0 ? 1 : 0})}>
              {product.hasAyah === 0 ? <FaToggleOff className="off" /> :
                <FaToggleOn className="on" />
              }
            </button>
          </div>

          <button type="submit" disabled={submitting} className="good buttonNoBorder">
            {editingProductId ?
              <>
                {submitting ?
                  <ImSpinner2 className="smallspinner" /> :
                  <FiRotateCw className="icon" />
                } تحديث اللوحة
              </>
            : <>
                {submitting ?
                  <ImSpinner2 className="smallspinner" /> :
                  <FiSave className="icon" />
                } حفظ اللوحة
              </>
            }
          </button>
        </form>
      : addProduct ? <p className="warn">حدث خطأ اثناء تحميل المقاسات. حاول مرة اخرى</p> : ""}

      {isLoadingInitial ? (
        <PanelSkeleton count={2} />
      ) : viewMode === "table" && !addProduct ? (
        products.length > 0 ? (
          <div className={styles.tableWrap}>
            <table className={styles.productsTable}>
              <thead>
                <tr>
                  <th>الصورة</th>
                  <th>اللوحة</th>
                  <th>الفئات</th>
                  <th>اللون</th>
                  <th>النوع</th>
                  <th>المقاسات</th>
                  <th>السعر (Matte)</th>
                  <th>الحالة</th>
                  <th>إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => {
                  const mainImg = product.images.find(img => img.is_main) ?? product.images[0];
                  const prices = (product.dimensions ?? []).map(d => Number(d.matte_price)).filter(n => !isNaN(n));
                  const minPrice = prices.length ? Math.min(...prices) : null;
                  const maxPrice = prices.length ? Math.max(...prices) : null;
                  const confirming = show[product.product_id];

                  return (
                    <tr key={product.product_id} className={product.is_deleted ? styles.rowDeleted : ""}>
                      <td>
                        <ProductImage url={mainImg?.image_url} alt={product.name} loading="lazy" className={styles.tableThumb} />
                      </td>
                      <td className={styles.tableName}>
                        <strong>{product.name}</strong>
                        <span>كود {product.code}</span>
                      </td>
                      <td>
                        <div className={styles.tableTags}>
                          {product.categories?.length
                            ? product.categories.map(cat => (
                                <span key={cat.id} className={`${styles.badge} ${styles.badgeCat}`}>{cat.name}</span>
                              ))
                            : "—"}
                        </div>
                      </td>
                      <td>{color.find(c => c.value === product.color)?.label ?? product.color}</td>
                      <td>
                        <div className={styles.tableTags}>
                          <span className={styles.tableChip}>{product.multi ? "لوحات متعددة" : "لوحة واحدة"}</span>
                          {product.has_ayah && <span className={styles.tableChip}>مع آية</span>}
                        </div>
                      </td>
                      <td>{product.dimensions?.length ?? 0}</td>
                      <td className={styles.tablePrice}>
                        {minPrice === null ? "—" : minPrice === maxPrice ? `${minPrice} ₪` : `${minPrice} – ${maxPrice} ₪`}
                      </td>
                      <td>
                        {product.is_deleted
                          ? <span className={`${styles.badge} ${styles.badgeDeleted}`}>محذوف</span>
                          : <span className={styles.statusActive}>نشطة</span>}
                      </td>
                      <td>
                        {confirming ? (
                          <div className={styles.tableConfirm}>
                            <button
                              className={styles.softBtn}
                              disabled={soft || hard}
                              onClick={() => handleSoftDelete(product)}
                            >{soft ? <ImSpinner2 className="smallspinner" /> : "حذف مؤقت"}</button>
                            <button
                              className={styles.hardBtn}
                              disabled={soft || hard}
                              onClick={() => handleHardDelete(product)}
                            >{hard ? <ImSpinner2 className="smallspinner" /> : "حذف نهائى"}</button>
                            <HiOutlineXCircle
                              className={styles.confirmClose}
                              onClick={() => setShow(prev => ({...prev, [product.product_id]: false}))}
                            />
                          </div>
                        ) : (
                          <div className={styles.tableActions}>
                            {product.is_deleted ? (
                              <button
                                disabled={restoring}
                                onClick={() => handleRestore(product)}
                                className={`${styles.actionBtn} ${styles.restoreBtn}`}
                              >
                                {restoring ? <ImSpinner2 className="smallspinner" /> : <FiRotateCw className={styles.restore} />} إعادة
                              </button>
                            ) : (
                              <>
                                <button
                                  onClick={() => { handleEdit(product); window.scrollTo({ top: 0, behavior: "smooth" }) }}
                                  disabled={submitting}
                                  className={`${styles.actionBtn} ${styles.editBtn}`}
                                  title="تعديل"
                                >
                                  <FiEdit /> تعديل
                                </button>
                                <button
                                  type="button"
                                  onClick={() => toggleShow(product.product_id)}
                                  className={styles.trashBtn}
                                  title="حذف"
                                >
                                  <FaRegTrashAlt className="delete cursor" />
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="warn">لا توجد لوحة تتوافق مع كلمات البحث</p>
        )
      ) : (
        <div className="panelcont">
          {products.length > 0 ? (
            products.map((product) => {
              // Main image (API sends true/false); fall back to the first image
              const mainImg = product.images.find(img => img.is_main) ?? product.images[0];
              const isExpanded = expanded[product.product_id];

              return (
                <div
                  key={product.product_id}
                  className={styles.card}
                  ref={el => panelRefs.current[product.product_id] = el}
                >
                  <div className={`upperPanel ${styles.cardHead}`}>
                    <ProductImage
                      url={mainImg?.image_url}
                      alt={product.name}
                      loading="lazy"
                      className={styles.thumb}
                      onClick={() => navigate(`/product/${product.product_id}`)}
                    />

                    <div className={styles.cardInfo}>
                      <p className={styles.cardName}>{product.name}</p>

                      <div className={styles.badges}>
                        <span className={`${styles.badge} ${styles.badgeCode}`}>كود {product.code}</span>
                        {product.categories?.length
                          ? product.categories.map(cat => (
                              <span key={cat.id} className={`${styles.badge} ${styles.badgeCat}`}>{cat.name}</span>
                            ))
                          : <span className={`${styles.badge} ${styles.badgeCat}`}>—</span>}
                        <span className={`${styles.badge} ${styles.badgeColor}`}>{color.find(c => c.value === product.color)?.label ?? product.color}</span>
                        {product.is_deleted ? (
                          <span className={`${styles.badge} ${styles.badgeDeleted}`}>محذوف</span>
                        ) : null}
                      </div>
                    </div>

                    <div className={styles.moreRow}>
                      <button className={styles.expandBtn} onClick={() => toggleExpand(product.product_id)}>
                        {isExpanded ?
                          <>
                            إخفاء التفاصيل
                            <MdKeyboardArrowUp className={styles.expandIcon} />
                          </>
                        : <>
                            عرض التفاصيل
                            <MdKeyboardArrowDown className={styles.expandIcon} />
                          </>
                        }
                      </button>

                      {/* Edit right from the list, without opening the details */}
                      {!product.is_deleted && (
                        <button
                          onClick={() => { handleEdit(product); window.scrollTo({ top: 0, behavior: "smooth" }) }}
                          disabled={submitting}
                          className={`${styles.actionBtn} ${styles.editBtn} ${styles.quickEditBtn}`}
                        >
                          <FiEdit /> تعديل
                        </button>
                      )}
                    </div>
                  </div>

                  {isExpanded && (
                    <div className={styles.expand}>
                      <div className={styles.descBlock}>
                        <p className={styles.descLabel}>الوصف</p>
                        <p className={styles.descText}>{product.description}</p>
                      </div>

                      <table className={styles.priceTable}>
                        <thead>
                          <tr>
                            <th>المقاس</th>
                            <th>المط</th>
                            <th>اللامع</th>
                          </tr>
                        </thead>
                        <tbody>
                          {/* Smallest to largest (by area, then width) */}
                          {product.dimensions && [...product.dimensions]
                            .sort((a, b) => a.width * a.height - b.width * b.height || a.width - b.width)
                            .map((dim) => (
                            <tr key={dim.id}>
                              <td>{dim.width} × {dim.height}</td>
                              <td>{dim.matte_price}</td>
                              <td>{dim.gloss_price}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      <div className={styles.gallery}>
                        {product.images.map(img => (
                          <ProductImage
                            key={img.image_id}
                            url={img.image_url}
                            alt={product.name}
                            loading="lazy"
                          />
                        ))}

                        {product.video.video_url &&
                          <video
                            src={mediaUrl(product.video.video_url)}
                            controls
                            controlsList="nodownload noremoteplayback"
                            preload="metadata"
                            playsInline
                          >
                            <p>Your browser doesn't support HTML video</p>
                          </video>
                        }
                      </div>

                      {show[product.product_id] ? (
                        <div className={styles.confirmBar}>
                          <span className={styles.confirmText}>تأكيد حذف {product.name}؟</span>
                          <button
                            className={styles.softBtn}
                            disabled={soft || hard}
                            onClick={() => handleSoftDelete(product)}
                          >{soft ? <ImSpinner2 className="smallspinner" /> : "حذف مؤقت"}</button>
                          <button
                            className={styles.hardBtn}
                            disabled={soft || hard}
                            onClick={() => handleHardDelete(product)}
                          >{hard ? <ImSpinner2 className="smallspinner" /> : "حذف نهائى"}</button>
                          <HiOutlineXCircle
                            className={styles.confirmClose}
                            onClick={() => setShow(prev => ({...prev, [product.product_id]: false}))}
                          />
                        </div>
                      ) : (
                        <div className={styles.actionRow}>
                          {product.is_deleted ?
                            <button
                              disabled={restoring}
                              onClick={() => handleRestore(product)}
                              className={`${styles.actionBtn} ${styles.restoreBtn}`}
                            >
                              {restoring ? <ImSpinner2 className="smallspinner" /> :
                                <FiRotateCw className={styles.restore} />
                              } إعادة
                            </button>
                          : <>
                              <button
                                type="button"
                                onClick={() => toggleShow(product.product_id)}
                                className={styles.trashBtn}
                              >
                                <FaRegTrashAlt className="delete cursor" />
                              </button>
                            </>
                          }
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })
          ) : (
            <p className="warn">لا توجد لوحة تتوافق مع كلمات البحث</p>
          )}
        </div>
      )}

    {totalPages > 1 &&
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        disabled={isLoadingInitial}
      />
    }
    </div>
  );
};

export default ProductsPanel;
